import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { Customer } from '../types';
import { customerService } from '../services/customerService';
import { customToast } from '../utils/customToast';
import { X, DollarSign, User } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onPaymentCompleted: () => void;
  isLoading?: boolean;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  customer,
  onPaymentCompleted
  , isLoading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [isClosing, setIsClosing] = useState(false);
  const [paymentType, setPaymentType] = useState<'partial' | 'full'>('partial');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens with a new customer
  useEffect(() => {
    if (isOpen && customer) {
      setPaymentType('partial');
      setAmount('');
      setNotes('');
    }
  }, [isOpen, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customer) return;

    const currentDues = parseFloat(customer?.balance_amount?.toString() || '0') || 0;
    
    if (paymentType === 'partial') {
      const paymentAmount = parseFloat(amount);
      if (!paymentAmount || paymentAmount <= 0) {
        customToast.error('Please enter a valid payment amount');
        return;
      }
      if (paymentAmount > currentDues) {
        customToast.error('Payment amount cannot exceed current dues');
        return;
      }
    }

    setLoading(true);
    try {
      if (paymentType === 'full') {
        await customerService.clearDues(customer.id);
        customToast.success('All dues cleared successfully!');
      } else {
        await customerService.addPayment(customer.id, {
          amount: parseFloat(amount),
          notes
        });
        customToast.success(`Payment of ${formatCurrency(amount)} added successfully!`);
      }
      
      onPaymentCompleted();
      handleCloseWithAnimation();
    } catch (error) {
      console.error('Payment error:', error);
      customToast.error('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseWithAnimation = () => {
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 150);
    }
  };

  const handleClose = () => {
    if (!loading) {
      handleCloseWithAnimation();
    }
  };

  if (!isOpen && !isClosing) return null;

  const currentDues = parseFloat(customer?.balance_amount?.toString() || '0') || 0;

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[99999] overflow-y-auto ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={(e) => e.target === e.currentTarget && !loading && handleClose()}
    >
      <div className={`w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-lg shadow-2xl my-auto ${
        isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
      } ${theme === 'dark' ? 'bg-gray-800 border border-gray-600' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 border-b flex-shrink-0 ${
          theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-slate-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <DollarSign className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  Process Payment
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {paymentType === 'full' ? 'Clear all customer dues' : 'Make partial payment'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className={`p-2 rounded-lg transition-colors disabled:opacity-50 touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-2.5 sm:p-3 md:p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          {isLoading ? (
            <div className="space-y-4">
              <div className={`p-2.5 sm:p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-slate-50'}`}>
                <div className="h-4 w-1/3 rounded-md bg-gray-300 dark:bg-slate-700 animate-pulse" />
                <div className="mt-3 h-12 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
              </div>
              <div className="rounded-lg p-3">
                <div className="h-24 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
              </div>
            </div>
          ) : (
          <div className="space-y-3 sm:space-y-4 animate-content-fade">{/* Customer Information */}
            <div className="space-y-2.5 sm:space-y-3">
              <h3 className={`text-sm sm:text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-slate-900'
              }`}>
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Customer Information</span>
              </h3>
              
              <div className={`p-2.5 sm:p-3 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-slate-50'
              }`}>
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <div>
                      <p className={`font-medium text-sm ${
                        theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`}>
                        {customer?.name}
                      </p>
                      {customer?.phone && (
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                        }`}>
                          Phone: {customer.phone}
                        </p>
                      )}
                    </div>
                    <div className="text-left sm:text-right">
                      <p className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                      }`}>
                        Current Dues
                      </p>
                      <p className="text-base sm:text-lg font-bold text-red-600">
                        {formatCurrency(currentDues)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="space-y-2.5 sm:space-y-3">
              <h3 className={`text-sm sm:text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-slate-900'
              }`}>
                <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Payment Details</span>
              </h3>

              {/* Payment Type Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 sm:mb-3 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Payment Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentType('partial')}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all touch-manipulation ${
                      paymentType === 'partial'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : theme === 'dark'
                          ? 'border-gray-600 bg-gray-700/50 hover:bg-gray-700'
                          : 'border-slate-300 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-center">
                      <p className={`font-medium text-sm sm:text-base ${
                        paymentType === 'partial' ? 'text-blue-600' : 
                        theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`}>
                        Partial Payment
                      </p>
                      <p className={`text-xs ${
                        paymentType === 'partial' ? 'text-blue-500' : 
                        theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                      }`}>
                        Pay specific amount
                      </p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setPaymentType('full')}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all touch-manipulation ${
                      paymentType === 'full'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : theme === 'dark'
                          ? 'border-gray-600 bg-gray-700/50 hover:bg-gray-700'
                          : 'border-slate-300 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-center">
                      <p className={`font-medium text-sm sm:text-base ${
                        paymentType === 'full' ? 'text-green-600' : 
                        theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`}>
                        Clear All Dues
                      </p>
                      <p className={`text-xs ${
                        paymentType === 'full' ? 'text-green-500' : 
                        theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                      }`}>
                        Pay {formatCurrency(currentDues)}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Amount Input (for partial payments) */}
              {paymentType === 'partial' && (
                <div>
                  <label htmlFor="amount" className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                  }`}>
                    Payment Amount *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                      }`}>{currencySymbol}</span>
                    </div>
                    <input
                      id="amount"
                      type="number"
                      min="0"
                      max={currentDues}
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className={`w-full pl-7 sm:pl-8 pr-3 sm:pr-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                      }`}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                  }`}>
                    Maximum: {formatCurrency(currentDues)}
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label htmlFor="notes" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                  }`}
                  placeholder="Add payment notes..."
                />
              </div>
            </div>
          </div>

          )}

          {/* Action Buttons */}
          <div className={`flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6 border-t px-4 sm:px-6 pb-4 sm:pb-6 ${
            theme === 'dark' ? 'border-gray-600' : 'border-slate-200'
          }`}>
            {loading ? (
              <>
                <div className={`w-full sm:w-auto h-12 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`} />
                <div className={`w-full sm:w-auto h-12 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`} />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 touch-manipulation ${
                    theme === 'dark'
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50 focus:ring-slate-500'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || (paymentType === 'partial' && (!amount || parseFloat(amount) <= 0))}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 text-white rounded-xl focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 touch-manipulation ${
                    paymentType === 'full'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  }`}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  <span>
                    {loading ? 'Processing...' : 
                     paymentType === 'full' ? `Clear ${formatCurrency(currentDues)}` : 
                     `Pay ${formatCurrency(amount || 0)}`}
                  </span>
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default PaymentModal;
